import { defineComponent as _defineComponent } from 'vue'
import DatePicker from 'vue2-datepicker';
import { computed, ref, watch, nextTick } from 'vue'
import 'vue2-datepicker/index.css';
import TextField from '../text-inputs/TextField.vue';
import ButtonCommon from '../buttons/ButtonCommon.vue'
import {getDateFromLocalDate} from "../../utils/helpers";

type Props = {
  range: boolean,
  sideBar?: boolean,
  monthNames: string[],
  weekDayNames: string[],
  sidebarOptions?: string[],
  cancelText: string,
  doneText: string,
  value: string | string[] | Date,
  placeholderText: string,
  disableFutureDates?: boolean,
  minDate?: Date | number, // storybook require number to work
  dark?: boolean,
  appendToBody?: boolean,
  fullWidth?: boolean,
  startWithPlaceholder?: boolean,
  hideDetails?: boolean
  disabled?: boolean
}


export default /*#__PURE__*/_defineComponent({
  __name: 'DatePicker',
  props: {
    range: { type: Boolean },
    sideBar: { type: Boolean },
    monthNames: null,
    weekDayNames: null,
    sidebarOptions: null,
    cancelText: null,
    doneText: null,
    value: null,
    placeholderText: null,
    disableFutureDates: { type: Boolean },
    minDate: null,
    dark: { type: Boolean },
    appendToBody: { type: Boolean, default: true },
    fullWidth: { type: Boolean },
    startWithPlaceholder: { type: Boolean },
    hideDetails: { type: Boolean, default: false },
    disabled: { type: Boolean }
  },
  emits: ["input", "close", "open"],
  setup(__props: any, { emit }: { emit: ({
  (e: "input", value: string | string[]): void;
  (e: "close"): void;
  (e: "open"): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { range: boolean, sideBar?: boolean, monthNames: string[], weekDayNames: string[], sidebarOptions?: string[], cancelText: string, doneText: string, value: string | string[] | Date, placeholderText: string, disableFutureDates?: boolean, minDate?: Date | number, dark?: boolean, appendToBody: boolean, fullWidth?: boolean, startWithPlaceholder?: boolean, hideDetails: boolean, disabled?: boolean };


const valueProp = computed(() => props.value as (string | string[]));
const openState = ref<boolean>(false);
const showPlaceholder = ref<boolean | undefined>(props.startWithPlaceholder);

const defaultValue = computed({
  get: () => {
    if (openState.value) {
      return displayText.value ? displayText.value : ''
    } else {
      return props.range ? format(getDateFromLocalDate(valueProp.value[0])) + ' ~ ' + format(getDateFromLocalDate(valueProp.value[1])) : format(getDateFromLocalDate(valueProp.value));
    }
  },
  set: (newValue: string) => {
    displayText.value = newValue;
  }
});

const dateValue = computed({
  get: () => {
    if (props.range) {
      const dates = displayText.value.split(' ~ ');
      const startDate = isValidDate(dates[0]) ? getDateFromLocalDate(dates[0]) : undefined;
      const endDate = isValidDate(dates[1]) ? getDateFromLocalDate(dates[1]) : undefined;
      if (startDate && endDate) {
        return [startDate, endDate];
      } else if (startDate) {
        return [startDate, startDate];
      }
    }
    return getDateFromLocalDate(displayText.value);
  },
  set: (newValue: Date | Date[]) => {
    if (Array.isArray(newValue) && newValue[0] !== null && newValue[1] !== null) {
      displayText.value = format(newValue[0]) + ' ~ ' + format(newValue[1]);
    } else if (!Array.isArray(newValue) && newValue !== null) {
      displayText.value = format(newValue);
    }
  }
});

const rangeDiff = ref<number | null>(null);
const pickStartDate = ref<Date | null>(null);
const pickEndDate = ref<Date | null>(null);
const rangeType = ref<string | null>(null);
const key = ref<number>(0);
const error = ref<boolean>(false);
const displayText = ref<string>('');
const intermediateDate = ref<string | string[]>();

const lang = computed(() => {
    return {
      formatLocale: {
        firstDayOfWeek: 1,
        months: props.monthNames,
        weekdaysMin: props.weekDayNames,
      },
      monthFormat: 'MMMM',
    }
  }
);

const getPopupClass = computed(() => {
  const className = [];
  if (props.range && rangeDiff.value) {
    switch (rangeDiff.value) {
      case 0:
        className.push('rangeDifferenceZeroClass');
        break;
      case 1:
        className.push('rangeDifferenceOneClass');
        break;
      default:
        className.push('rangeDifferenceClass');
    }
  } else {
    className.push('mx-datepicker-popup-range');
  }

  if (props.dark !== undefined && props.dark === true && !props.range) {
    className.push('mx-datepicker-popup__dark');
  }

  if (!pickEndDate.value && pickStartDate.value !== null) {
    className.push('remove-radius');
  }
  if (pickEndDate.value != null && pickEndDate.value.getDay() == 1) {
    nextTick(() => {
      document.getElementsByClassName("cell active")[1].classList.add("remove-end-radius");
    });
  }
  return className;
});


const getShortcuts = computed(() => {
  return [
    {
      text: props.sidebarOptions[0],
      value: 'today'
    },
    {
      text: props.sidebarOptions[1],
      value: 'yesterday'
    },
    {
      text: props.sidebarOptions[2],
      value: 'thisWeek'
    },
    {
      text: props.sidebarOptions[3],
      value: 'lastWeek'
    },
    {
      text: props.sidebarOptions[4],
      value: 'last2Week'
    },
    {
      text: props.sidebarOptions[5],
      value: 'thisMonth'
    },
    {
      text: props.sidebarOptions[6],
      value: 'lastMonth'
    },
    {
      text: props.sidebarOptions[7],
      value: 'custom'
    },
  ];
});

function onClear() {
  rangeType.value = null;
}

function onOpen() {
  openState.value = true;
  error.value = false;
  intermediateDate.value = valueProp.value;
  emit("open");

  if (!Array.isArray(valueProp.value)) {
    rangeType.value = null;
    displayText.value = format(getDateFromLocalDate(valueProp.value));
  } else if (valueProp.value[0] && valueProp.value[1]) {
    const start =  valueProp.value[0];
    const end = valueProp.value[1];
    selectRangeType(getDateFromLocalDate(start), getDateFromLocalDate(end));
    displayText.value = format(getDateFromLocalDate(start)) + ' ~ ' + format(getDateFromLocalDate(end));
  }
}

function onAppendIconClick() {
  if(openState.value) {
    onPanelClose();
  } else {
    onOpen();
  }
}

function onInputError(text: string) {
  error.value = true
  key.value++;
}

function onChange() {
  pickStartDate.value = null;
  pickEndDate.value = null;
  showPlaceholder.value = false;
}

function onPick(val: Date) {
  if (!props.range) {
    displayText.value = format(val);
    return;
  }

  if (pickStartDate.value && pickEndDate.value) {
    pickStartDate.value = null;
    pickEndDate.value = null;
    rangeType.value = null;
  }

  if (!pickStartDate.value) {
    pickStartDate.value = val;
    rangeType.value = null;
    if (props.range) {
      displayText.value = format(pickStartDate.value);
    }
    return;
  }
  if (!pickEndDate.value) {
    pickEndDate.value = val;
  }

  const today = new Date();
  if (pickStartDate.value.getDate() === pickEndDate.value.getDate()) {
    const yesterday = new Date(Date.now() - 24 * 3600 * 1000);
    if (pickStartDate.value.getDate() === today.getDate()) {
      rangeType.value = 'today';
      rangeDiff.value = 0;
      displayText.value = `${format(pickStartDate.value)} ~ ${format(pickEndDate.value)}`;
      return;
    } else if (pickStartDate.value.getDate() === yesterday.getDate()) {
      displayText.value = `${format(pickStartDate.value)} ~ ${format(pickEndDate.value)}`;
      rangeType.value = 'yesterday';
      rangeDiff.value = 0;
      return;
    }
  }

  if (pickStartDate.value > pickEndDate.value) {
    const tempDate = pickStartDate.value;
    pickStartDate.value = pickEndDate.value;
    pickEndDate.value = tempDate;
  }

  if (props.range) {
    displayText.value = `${format(pickStartDate.value)} ~ ${format(pickEndDate.value)}`;
  }

  selectRangeType(pickStartDate.value, pickEndDate.value);
}

function selectRangeType(startDate: Date, endDate: Date) {
  const diff = startDate.getDate() - endDate.getDate();
  rangeDiff.value = diff < 0 ? diff * -1 : diff;

  if (rangeDiff.value === 0) {
    const today = new Date();
    const yesterday = new Date(Date.now() - 24 * 3600 * 1000);
    if (startDate.getDate() === today.getDate()) {
      rangeType.value = 'today';
    } else if (startDate.getDate() === yesterday.getDate()) {
      rangeType.value = 'yesterday';
    }
    return;
  }

  const thisWeek = getThisWeekDate();
  if (startDate.getDate() === thisWeek.first.getDate() && endDate.getDate() === thisWeek.end.getDate()) {
    rangeType.value = 'thisWeek';
    return;
  }

  const lastWeekDate = getLastWeekDate('lastWeek');
  if (startDate.getDate() === lastWeekDate.first.getDate() && endDate.getDate() === lastWeekDate.end.getDate()) {
    rangeType.value = 'lastWeek';
    return;
  }

  const last2WeekDate = getLastWeekDate('last2Week');
  if (startDate.getDate() === last2WeekDate.first.getDate() && endDate.getDate() === last2WeekDate.end.getDate()) {
    rangeType.value = 'last2Week';
    return;
  }

  const lastMonthDate = getMonthDate('lastMonth');
  if (startDate.getDate() === lastMonthDate.first.getDate() && endDate.getDate() === lastMonthDate.end.getDate()) {
    rangeType.value = 'lastMonth';
    return;
  }

  const thisMonthDate = getMonthDate('thisMonth');
  if (startDate.getDate() === thisMonthDate.first.getDate() && endDate.getDate() === thisMonthDate.end.getDate()) {
    rangeType.value = 'thisMonth';
    return;
  }

  rangeType.value = 'custom';
}

function getMonthDate(type: string) {
  const first = new Date((new Date()).getFullYear(), (new Date()).getMonth() - ((type === 'lastMonth') ? 1 : 0), 1);
  const end = (type === 'lastMonth') ? new Date((new Date()).getFullYear(), (new Date()).getMonth(), 0) : new Date();
  return { first, end };
}

function getLastWeekDate(type: string) {
  const today = new Date();
  const diff = (type === 'last2Week') ? 13 : 6;
  const first = new Date(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay() - diff);
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay());
  return { first, end };
}

function getThisWeekDate() {
  const today = new Date();
  const first = new Date(today.setDate(today.getDate() - today.getDay() + 1));
  const end = new Date();
  return { first, end };
}

function selectDate(type: string, emit: any) {
  let isCustom = false;
  if (type === "custom") {
    isCustom = true;
    type = !rangeType.value ? "today" : rangeType.value;
  }
  switch (type) {
    case 'today': selectToday(emit); break;
    case 'yesterday': selectYesterday(emit); break;
    case 'lastWeek': selectLastWeek('lastWeek', emit); break;
    case 'last2Week': selectLastWeek('last2Week', emit); break;
    case 'thisWeek': selectThisWeek(emit); break;
    case 'thisMonth': selectMonth('thisMonth', emit); break;
    case 'lastMonth': selectMonth('lastMonth', emit); break;
  }
  if (isCustom) {
    rangeType.value = "custom";
  }
}

function selectToday(emit: any) {
  const startDate = new Date();
  displayText.value = format([startDate, startDate]);
  emit([startDate, startDate]);
  rangeDiff.value = 0;
  rangeType.value = 'today';
}

function selectYesterday(emit: any) {
  const startDate = new Date(Date.now() - 24 * 3600 * 1000);
  displayText.value = format([startDate, startDate]);
  emit([startDate, startDate]);
  rangeDiff.value = 0;
  rangeType.value = 'yesterday';
}

function selectLastWeek(type: string, emit: any) {
  const vDate = getLastWeekDate(type);
  displayText.value = format([vDate.first, vDate.end]);
  emit([vDate.first, vDate.end]);
  rangeDiff.value = 7;
  rangeType.value = type;
}

function selectThisWeek(emit: any) {
  const vDate = getThisWeekDate();
  rangeDiff.value = vDate.end.getDate() - vDate.first.getDate();
  displayText.value = format([vDate.first, vDate.end]);
  emit([vDate.first, vDate.end]);
  rangeType.value = 'thisWeek';
}

function selectMonth(type: string, emit: any) {
  const vDate = getMonthDate(type);
  displayText.value = format([vDate.first, vDate.end]);
  emit([vDate.first, vDate.end]);
  rangeDiff.value = vDate.end.getDate() - vDate.first.getDate();
  rangeType.value = type;
}

function cancel() {
  if (!dateValue.value || dateValue.value.toString() === ',') {
    rangeType.value = null;
  }

  if (Array.isArray(intermediateDate.value)) {
    displayText.value = intermediateDate.value[0] + ' ~ ' + intermediateDate.value[1];
  } else {
    displayText.value = intermediateDate.value;
  }
  openState.value = false;
  key.value++;
}

function onPanelClose() {
  openState.value = false;
  emit("close");
}

function onConfirm(val: Date) {
  if (!intermediateDate.value) {
    return;
  }

  if (!displayText.value.length) {
    emit("input", intermediateDate.value);
    return;
  }

  if (props.range) {
    const dates = displayText.value.split(' ~ ');
    if (!dates.length) {
      emit("input", intermediateDate.value);
      return;
    }
    const startDate = isValidDate(dates[0]) ? dates[0].trim() : undefined;
    const endDate = isValidDate(dates[1]) ? dates[1].trim() : undefined;

    if (startDate && endDate) {
      const startDateVal = getDateFromLocalDate(startDate);
      const endDateVal = getDateFromLocalDate(endDate);
      if (startDateVal > endDateVal || endDateVal.getTime() > (new Date()).getTime()) {
        emit("input", intermediateDate.value);
      } else {
        emit("input", dates);
      }
    } else {
      emit("input", intermediateDate.value);
    }
  } else {
    const isValid = isValidDate(displayText.value);
    if (isValid) {
      emit("input", displayText.value);
    } else {
      emit("input", intermediateDate.value);
    }
  }
  openState.value = false;
}

function format(val: Date | Date[]) {
  if (Array.isArray(val)) {
    val[0].setHours(12, 0, 0, 0)
    val[1].setHours(12, 0, 0, 0)
    return `${val[0].toISOString().slice(0, 10)} ~ ${val[1].toISOString().slice(0, 10)}`;
  }
  val.setHours(12, 0, 0, 0)
  return val.toISOString().slice(0, 10);
}

watch(
  () => displayText.value,
  () => {
    if (displayText.value === undefined) {
      return;
    }
    const len = displayText.value.length;

    if (props.range) {
      if ([4, 7, 17, 20].includes(len)) {
        displayText.value += "-";
      } else if (len === 11) {
        displayText.value += "~";
      }
    } else {
      if ([4, 7].includes(len)) {
        displayText.value += "-";
      }
    }
    updateRange(displayText.value);
  }
);

function updateRange(val: string) {
  if (props.range) {
    const dates = val.split(' ~ ');
    const startDate = isValidDate(dates[0]) ? dates[0] : undefined;
    const endDate = isValidDate(dates[1]) ? dates[1] : undefined;
    if (startDate && endDate) {
      const startDateVal = getDateFromLocalDate(startDate)
      const endDateVal = getDateFromLocalDate(endDate)
      if (startDateVal.getDate() > endDateVal.getDate()) {
        return;
      } else {
        selectRangeType(startDateVal, endDateVal);
        return;
      }
    }
  }
}

function isValidDate(val: string) {
  if (!val) {
    return false;
  }
  return !isNaN(Date.parse(val.trim())) && val.trim().length === 10;
}

function disabledDate(val: Date) {
  if(!props.disableFutureDates && !props.minDate) {
    return false;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  val.setHours(0, 0, 0, 0);

  if (props.disableFutureDates && val > today) {
    return true;
  }

  if (props.minDate ) {
    const minDate = props.minDate instanceof Date ? props.minDate : new Date(props.minDate);
    minDate.setHours(0, 0, 0, 0);
    if (val >= minDate) {
      return false;
    }else{
      return true;
    }
  }
  return false;
}

return { __sfc: true,props, valueProp, openState, showPlaceholder, emit, defaultValue, dateValue, rangeDiff, pickStartDate, pickEndDate, rangeType, key, error, displayText, intermediateDate, lang, getPopupClass, getShortcuts, onClear, onOpen, onAppendIconClick, onInputError, onChange, onPick, selectRangeType, getMonthDate, getLastWeekDate, getThisWeekDate, selectDate, selectToday, selectYesterday, selectLastWeek, selectThisWeek, selectMonth, cancel, onPanelClose, onConfirm, format, updateRange, isValidDate, disabledDate, DatePicker, TextField, ButtonCommon }
}

})